import isEmpty from "lodash.isempty";
import PropTypes from "prop-types";
import RedirectToCatalog from "./redirectToCatalog";
import LimitedCatalog from "./limitedCatalog";
import useAnalytics from "../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import { dateToDMY, getDates } from "../../../utils/common/deliveryDate";
/**
 * RedirectionToPLPComponent handles the conditional rendering of the catalog page or limited catalog
 * page based on provided blocked dates, delivery dates, cutoff timestamp, and other related data.
 *
 * @param {Object} props - Component props
 * @param {string} props.blockedDates - A string of blocked delivery dates in a specific format
 * @param {boolean} props.displayCatalogContent - Flag to determine whether to show catalog content
 * @param {string | number} props.cutoffTimestamp - The cutoff timestamp for deliveries
 * @param {boolean} props.isCalender - Flag to determine if the calendar is active or not
 * @param {Array<string>} props.deliveryDatesList - List of available delivery dates
 * @param {Object} props.samedayMsgContent - Content related to same-day messages (footer, URLs, anchor text)
 * @param {Object} props.limitedCatalogContent - Content related to the limited catalog view (webmessage, links, promo)
 * @param {string} props.pincode - Pincode for delivery location
 *
 * @returns {JSX.Element} - Conditionally renders either the RedirectToCatalog component or the LimitedCatalog component
 */
const RedirectionToPLPComponent = ({
  blockedDates,
  isDisplayCatalogContent,
  isCutoffTimestamp,
  deliveryDatesList,
  samedayMsgContent,
  limitedCatalogContent,
  pincode,
  cleverTapData,
}) => {
  const jsonBlockedDates =
    blockedDates?.length > 0 ? JSON.parse(blockedDates.replace(/(\d{1,2})-(\d{1,2})-(\d{4})/g, '"$&"')) : [];
  const isLimitedCatalog = isEmpty(limitedCatalogContent);
  const isRedirectToCatalog = !getDates(jsonBlockedDates) || !isDisplayCatalogContent || isLimitedCatalog;

  const { trackEvent } = useAnalytics();
  /**
   * click on limit catalog
   */
  const onClickOverCatalog = () => {
    const cleverTapSameDayData = {
      ...cleverTapData,
      is_limited_catalogue: isRedirectToCatalog ? false : true,
      is_on_calendar: false,
    };

    trackEvent(cleverTapCommonEvents.wantSameDayClicked, cleverTapSameDayData, ["clevertap"]);
  };

  return (
    <>
      {isRedirectToCatalog ? (
        deliveryDatesList.length > 0 &&
        deliveryDatesList.indexOf(dateToDMY(new Date())) === -1 &&
        isCutoffTimestamp && (
          <RedirectToCatalog
            message={samedayMsgContent?.calenderFooterMsg}
            href={`${samedayMsgContent?.calenderFooterUrl}&pincode=${pincode}`}
            linkText={samedayMsgContent?.calenderFooterAnchortext}
            onClickOverCatalog={onClickOverCatalog}
          />
        )
      ) : (
        <LimitedCatalog
          message={limitedCatalogContent?.webmessage}
          href={`${limitedCatalogContent?.link}?promo=${limitedCatalogContent?.promo}`}
          image={limitedCatalogContent?.image}
          backgroundColor={limitedCatalogContent?.gradientColor}
          textColor={limitedCatalogContent?.textColor}
          onClickOverCatalog={onClickOverCatalog}
        />
      )}
    </>
  );
};

RedirectionToPLPComponent.propTypes = {
  blockedDates: PropTypes.string.isRequired,
  isDisplayCatalogContent: PropTypes.bool.isRequired,
  isCutoffTimestamp: PropTypes.bool.isRequired,
  isCalender: PropTypes.bool,
  deliveryDatesList: PropTypes.arrayOf(PropTypes.string).isRequired,
  samedayMsgContent: PropTypes.shape({
    calenderFooterMsg: PropTypes.string,
    calenderFooterUrl: PropTypes.string,
    calenderFooterAnchortext: PropTypes.string,
  }).isRequired,
  limitedCatalogContent: PropTypes.shape({
    webmessage: PropTypes.string,
    link: PropTypes.string,
    promo: PropTypes.string,
    image: PropTypes.string,
    gradientColor: PropTypes.object,
    textColor: PropTypes.string,
  }),
  pincode: PropTypes.string.isRequired,
  cleverTapData: PropTypes.object,
};

export default RedirectionToPLPComponent;
